import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 40, left: 50 };
const ROW_HEIGHT = 50;
const DOT_RADIUS = 4;
const DOT_HOVER_RADIUS = 7;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function BeeswarmPlot({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const processed = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}

    const byState = d3.group(data, (d) => d.state);
    const states = Array.from(byState.keys()).sort();
    const stateCounts = {};
    const stateMedians = {};

    states.forEach((st) => {
      const items = byState.get(st);
      stateCounts[st] = items.length;
      stateMedians[st] = d3.median(items, (d) => d.price);
    });

    return { byState, states, stateCounts, stateMedians };
  }, [data]);

  useEffect(() => {
    if (!processed) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { byState, states, stateCounts, stateMedians } = processed;

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = states.length * ROW_HEIGHT;
    const height = innerH + MARGIN.top + MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const priceExtent = d3.extent(data, (d) => d.price);
    const xPadding = (priceExtent[1] - priceExtent[0]) * 0.08 || 5;
    const x = d3.scaleLinear()
      .domain([priceExtent[0] - xPadding, priceExtent[1] + xPadding])
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(states)
      .range([0, innerH])
      .padding(0.1);

    const colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(states);

    // Grid lines
    const xTicks = x.ticks();
    g.selectAll('.grid-v')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).ticks(8).tickSize(0).tickFormat((d) => `$${d}`))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // State labels on the left
    states.forEach((st) => {
      const bandCenter = y(st) + y.bandwidth() / 2;
      g.append('text')
        .attr('x', -8)
        .attr('y', bandCenter)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('fill', '#374151')
        .attr('font-weight', 500)
        .text(`${st} (${stateCounts[st]})`);
    });

    // Median tick marks
    states.forEach((st) => {
      const median = stateMedians[st];
      const bandCenter = y(st) + y.bandwidth() / 2;
      g.append('line')
        .attr('x1', x(median))
        .attr('x2', x(median))
        .attr('y1', bandCenter - 6)
        .attr('y2', bandCenter + 6)
        .attr('stroke', '#1f2937')
        .attr('stroke-width', 2);
    });

    // Beeswarm force simulation per state
    const allNodes = [];

    states.forEach((st) => {
      const items = byState.get(st);
      const bandCenter = y(st) + y.bandwidth() / 2;

      const nodes = items.map((d) => ({
        ...d,
        targetX: x(d.price),
        targetY: bandCenter,
        x: x(d.price),
        y: bandCenter,
        stateColor: colorScale(st),
      }));

      // Run force simulation synchronously
      const simulation = d3.forceSimulation(nodes)
        .force('x', d3.forceX((d) => d.targetX).strength(1))
        .force('y', d3.forceY((d) => d.targetY).strength(0.3))
        .force('collide', d3.forceCollide(DOT_RADIUS + 1))
        .stop();

      for (let i = 0; i < 100; i++) {
        simulation.tick();
      }

      allNodes.push(...nodes);
    });

    // Draw dots
    g.selectAll('.dot')
      .data(allNodes)
      .enter()
      .append('circle')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', DOT_RADIUS)
      .attr('fill', (d) => d.stateColor)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8)
      .style('cursor', 'pointer')
      .on('mouseenter', function (event, d) {
        d3.select(this)
          .attr('r', DOT_HOVER_RADIUS)
          .attr('opacity', 1);
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          label: d.label,
          price: d.price,
        });
      })
      .on('mousemove', function (event, d) {
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          label: d.label,
          price: d.price,
        });
      })
      .on('mouseleave', function () {
        d3.select(this)
          .attr('r', DOT_RADIUS)
          .attr('opacity', 0.8);
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, processed]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Individual Price Distribution</div>
        <div style={styles.subtitle}>Each dot is an address, grouped by state with jitter to show density</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No individual price data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Individual Price Distribution</div>
      <div style={styles.subtitle}>Each dot is an address, grouped by state with jitter to show density</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.label}</div>
          <div style={{ color: '#2563eb', fontWeight: 600, marginTop: '2px' }}>
            ${tooltip.price.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}
